import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { WHATSAPP } from "../../lib/api";

const cards = [
  { tag: "Systematic Review · Q1", title: "Co-authorship in an ongoing systematic review", seats: 4, total: 12, spec: "Scopus · PubMed" },
  { tag: "Meta-analysis · Q2", title: "Meta-analysis of randomized controlled trials", seats: 7, total: 12, spec: "SCFHS · 5 points" },
  { tag: "Network MA", title: "Network meta-analysis with certified supervision", seats: 2, total: 12, spec: "Web of Science" },
];

const wordAnim = { hidden: { opacity: 0, y: 24 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.1 + i * 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }) };
const Words = ({ text, offset = 0, className }) => text.split(" ").map((w, i) => (
  <motion.span key={`${w}-${i}`} custom={i + offset} variants={wordAnim} initial="hidden" animate="show" className={className} style={{ display: "inline-block", marginInlineEnd: "0.28em" }}>{w}</motion.span>
));

export const Hero = () => {
  const { t, rtl } = useLang();
  const Arrow = rtl ? ArrowLeft : ArrowRight;
  const { scrollY } = useScroll();
  const ys = [useTransform(scrollY, [0, 600], [0, -60]), useTransform(scrollY, [0, 600], [0, -110]), useTransform(scrollY, [0, 600], [0, -30])];

  return (
    <section className="hero" data-testid="hero-section">
      <div className="container hero-inner">
        <div>
          <motion.div className="hero-eyebrow" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}><span className="pulse" />{t.hero.eyebrow}</motion.div>
          <h1 data-testid="hero-title"><Words text={t.hero.title1} /><br /><Words text={t.hero.title2} offset={6} className="shine" /></h1>
          <motion.p className="lead" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .6, duration: .7 }}>{t.hero.lead}</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .8, duration: .5 }}>
            <Link to="/opportunities" className="btn btn-primary" data-testid="hero-browse-button">{t.hero.cta}<Arrow size={18} /></Link>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" className="btn btn-outline" data-testid="hero-whatsapp-button"><MessageCircle size={18} />{t.hero.cta2}</a>
          </motion.div>
          <motion.div className="hero-tags" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: .7 }}>{t.hero.tags.map((x) => <span key={x}>{x}</span>)}</motion.div>
        </div>
        <div className="hero-visual" aria-hidden>
          {cards.map((c, i) => (
            <motion.div key={c.tag} className={`hero-card c${i + 1}`} style={{ y: ys[i] }} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .3 + i * .15, duration: .8, ease: [0.22, 1, 0.36, 1] }} whileHover={{ scale: 1.03 }}>
              <span className="hc-tag">{c.tag}</span><h3>{c.title}</h3>
              <div className="hc-seats">{Array.from({ length: c.total }).map((_, k) => <span key={k} className={k < c.total - c.seats ? "on" : ""} />)}</div>
              <div className="hc-meta"><span>{c.spec}</span><span>{c.seats} / {c.total} seats</span></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Marquee = () => {
  const { t } = useLang();
  return <div className="marquee" data-testid="marquee"><div className="marquee-track">{[...t.marquee, ...t.marquee].map((m, i) => <span className="marquee-item" key={i}><i />{m}</span>)}</div></div>;
};
